import { useEffect, useState } from "react";

import Button from "@/blueprint/components/Button/Button";
import Dialog from "@/blueprint/components/Dialog/Dialog";
import { CloseIcon, RadioActiveIcon, RadioIcon } from "@/consent/ConsentIcons";
import TextLink from "@/consent/TextLink";
import { CONSENT_SETTINGS, type ConsentTier } from "@/consent/consentContent";
import type { Consent } from "@/consent/consent";
import { useConsentStore } from "@/consent/useConsentStore";
import { cn } from "@/lib/utils";

// A stored state no tier can express falls back to the most private tier
// rather than silently widening consent.
const tierForConsent = (consent: Consent): string => {
  const match = CONSENT_SETTINGS.tiers.find(
    (tier) =>
      tier.choice.analytics === consent.analytics &&
      tier.choice.marketing === consent.marketing,
  );
  return match?.id ?? CONSENT_SETTINGS.tiers[0]!.id;
};

type TierRowProps = {
  tier: ConsentTier;
  selected: boolean;
  onSelect: () => void;
};

const TierRow = ({ tier, selected, onSelect }: TierRowProps) => (
  <label
    className={cn(
      "flex cursor-pointer items-center gap-s rounded-8 border p-m d:rounded-16",
      selected
        ? "border-functional-blue bg-functional-blue-subtle"
        : "border-tint-black-20 bg-brand-white hover:border-tint-black-70 hover:bg-brand-grey",
    )}
  >
    <input
      type="radio"
      name="cookie-tier"
      checked={selected}
      onChange={onSelect}
      className="peer sr-only"
    />
    <span className="size-24 shrink-0 text-content-primary peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-functional-blue">
      {selected ? <RadioActiveIcon /> : <RadioIcon />}
    </span>
    <span className="flex flex-col gap-[4px]">
      <span className="font-ppMori text-paragraph-md font-semibold text-content-primary">
        {tier.headline}
      </span>
      <span className="font-ppMori text-paragraph-sm text-content-primary">
        {tier.description}
      </span>
    </span>
  </label>
);

export default function ConsentPreferencesDialog() {
  const prefsOpen = useConsentStore((s) => s.prefsOpen);
  const closePrefs = useConsentStore((s) => s.closePrefs);
  const consent = useConsentStore((s) => s.consent);
  const savePreferences = useConsentStore((s) => s.savePreferences);

  const [selectedTier, setSelectedTier] = useState(() =>
    tierForConsent(consent),
  );

  // Re-seed on open so stale edits from a previous open don't linger.
  useEffect(() => {
    if (prefsOpen) setSelectedTier(tierForConsent(consent));
  }, [prefsOpen, consent]);

  const save = () => {
    const tier = CONSENT_SETTINGS.tiers.find((t) => t.id === selectedTier);
    if (!tier) return;
    savePreferences(tier.choice);
  };

  return (
    <Dialog
      open={prefsOpen}
      onClose={closePrefs}
      aria-labelledby="consent-settings-title"
      className="fixed inset-0 z-50"
    >
      <button
        type="button"
        aria-label={CONSENT_SETTINGS.close}
        onClick={closePrefs}
        className="fixed inset-0 bg-tint-black-20 animate-in fade-in-0"
      />
      <div className="pointer-events-none fixed inset-0 flex items-center justify-center p-[18px]">
        <div
          className={cn(
            "pointer-events-auto flex w-[334px] flex-col rounded-24 bg-brand-white shadow-card-pop duration-300 animate-in fade-in-0 slide-in-from-bottom-4",
            "gap-s p-m",
            "tab:w-[560px] tab:gap-m tab:p-xl",
            "sd:gap-20",
            "d:gap-l d:rounded-32",
          )}
        >
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={CONSENT_SETTINGS.close}
              onClick={closePrefs}
              className="relative size-24 text-content-primary after:absolute after:-inset-[10px] after:content-['']"
            >
              <CloseIcon />
            </button>
          </div>
          <h2
            id="consent-settings-title"
            className="font-ppMori text-h6 font-semibold text-content-primary"
          >
            {CONSENT_SETTINGS.title}
          </h2>
          <div
            role="radiogroup"
            aria-labelledby="consent-settings-title"
            className="flex flex-col gap-s tab:gap-m sd:gap-20 d:gap-l"
          >
            {CONSENT_SETTINGS.tiers.map((tier) => (
              <TierRow
                key={tier.id}
                tier={tier}
                selected={selectedTier === tier.id}
                onSelect={() => setSelectedTier(tier.id)}
              />
            ))}
          </div>
          <div className="flex flex-col items-center gap-[4px]">
            <Button size="md" onClick={save} className="w-full">
              {CONSENT_SETTINGS.save}
            </Button>
            <TextLink
              href={CONSENT_SETTINGS.privacyNoticeHref}
              onClick={closePrefs}
            >
              {CONSENT_SETTINGS.privacyNoticeLabel}
            </TextLink>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
